<template>
  <div>
    <slot name="data"></slot>
    <div v-if="currentPage || numberOfPages" class="zoox-table-pagination">
      <div class="pagination-controls">
        <slot name="precontrols"></slot>
        <div v-if="showItemsPerPage" class="mr-1 d-flex">
          <div class="pagination-text">
            {{ itemsPerPageText || $t('items_per_page') }}
          </div>

          <v-select
            dense
            hide-details
            class="custom-autocomplete pagination-text"
            append-icon=""
            :items="itemsPerPageOptions"
            :value="itemsPerPage"
            @change="$emit('change:items-per-page', $event)"
          ></v-select>
        </div>

        <span class="pagination-text">
          <span>{{ currentPage }}</span>

          <span>
            {{ $t('of') }}
          </span>

          <span>{{ formattedNumberOfPages }}</span>
        </span>

        <v-icon
          class="previous-icon"
          :disabled="currentPage <= 1"
          size="24px"
          @click="$emit('click:previous')"
        >
          {{ iconPrevious }}
        </v-icon>

        <v-icon
          class="next-icon"
          :disabled="currentPage >= numberOfPages"
          size="24px"
          @click="$emit('click:next')"
        >
          {{ iconNext }}
        </v-icon>

        <v-autocomplete
          v-if="hasLargeNumberOfPages"
          ref="autocomplete"
          class="custom-autocomplete pagination-text"
          hide-details
          dense
          append-icon=""
          :items="asyncItems"
          :search-input="searchInput"
          :value="currentPage"
          @focus="searchInput = ''"
          @update:search-input="onSearchInput"
          @change="onChange"
          @blur="onBlur"
        >
        </v-autocomplete>

        <v-autocomplete
          v-else
          ref="autocomplete"
          class="custom-autocomplete pagination-text"
          hide-details
          dense
          append-icon=""
          :items="pageItems"
          :search-input="searchInput"
          :value="currentPage"
          @focus="searchInput = ''"
          @update:search-input="onSearchInput"
          @change="onChange"
          @blur="onBlur"
        >
        </v-autocomplete>
      </div>
    </div>

    <div class="actions-slot">
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ZVisualPagination',
  props: {
    numberOfPages: {
      type: Number,
      required: false,
    },
    currentPage: {
      type: Number,
      required: false,
    },
    iconNext: {
      type: String,
      default: '$keyboard_arrow_right_rounded',
    },
    iconPrevious: {
      type: String,
      default: '$keyboard_arrow_left_rounded',
    },
    itemsPerPage: {
      type: Number,
      default: 5,
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [5, 10, 15, 20, 25, 50, 100],
    },
    showItemsPerPage: {
      type: Boolean,
      default: false,
    },
    itemsPerPageText: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    searchInput: '',
    asyncItems: [],
  }),

  computed: {
    hasLargeNumberOfPages() {
      return this.numberOfPages > 500
    },
    pageItems() {
      if (this.hasLargeNumberOfPages) {
        return []
      }

      const items = []
      if (this.numberOfPages == 0) {
        return items
      }

      for (let i = 1; i <= this.numberOfPages; i++) {
        items.push(i)
      }

      return items
    },
    formattedNumberOfPages() {
      return new Intl.NumberFormat('pt-BR').format(this.numberOfPages)
    },
  },

  methods: {
    onChangePage(page) {
      this.$emit('change:page', page)
    },

    onChange(evt) {
      if (!evt) return

      this.onChangePage(evt)
      this.$refs.autocomplete.blur()
    },

    onSearchInput(value) {
      this.searchInput = value ? value.toString() : this.currentPage.toString()

      if (this.hasLargeNumberOfPages) {
        this.updateAsyncItems()
      }
    },

    updateAsyncItems() {
      if (this.searchInput === '') {
        this.asyncItems = []
        return
      }

      const base = +this.searchInput
      if (base > this.numberOfPages) {
        this.asyncItems = []
        return
      }

      this.asyncItems = [base]
      const basePlus = parseInt(base + '0')
      const basePlus2 = parseInt(base + '00')

      for (let i = 0; i < 10; i++) {
        if (i + basePlus > this.numberOfPages) return
        this.asyncItems.push(i + basePlus)
      }

      for (let i = 0; i < 100; i++) {
        if (i + basePlus2 > this.numberOfPages) return
        this.asyncItems.push(i + basePlus2)
      }
    },

    onBlur() {
      if (!this.searchInput || isNaN(this.searchInput)) {
        setTimeout(() => {
          this.searchInput = this.currentPage.toString()
        }, 300)
      }
    },
  },

  i18n: {
    messages: {
      pt: {
        of: 'de',
        items_per_page: 'itens por página',
      },
      en: {
        of: 'of',
        items_per_page: 'items per page',
      },
      es: {
        of: 'de',
        items_per_page: 'artículos por página',
      },
      fr: {
        of: 'de',
        items_per_page: 'éléments par page',
      },
      tr: {
        of: 'ile',
        items_per_page: 'sayfa başına öğe',
      },
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.v-input__control > .v-input__slot) {
  height: 16px !important;
  min-height: 16px !important;
}

:deep(.v-select__selections) {
  color: var(--black-600) !important;
}

.zoox-table-pagination {
  text-align: end;
  padding: 5px 10px;
  position: absolute;
  bottom: 0;
  right: 0;

  .pagination-controls {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}

.actions-slot {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 5px 10px;
}

.previous-icon {
  margin: 0px 5px;
}

.pagination-text {
  font-size: 0.6875rem !important;
  line-height: 18px !important;
  color: var(--black-600);
  font-weight: 400;
  text-transform: uppercase;
}

.custom-autocomplete {
  margin: 0 10px;
  width: 55px;
  padding: 0;
  text-align: right !important;
}
</style>

<style lang="scss">
.custom-autocomplete {
  .v-select__slot {
    input {
      color: var(--black-600);
    }
  }
}
</style>
